import Phaser from 'phaser';
import { COLORS } from './theme';

export interface CoinIconOptions {
  size?: number;
}

// Two-tone orange coin with a monochrome cupcake silhouette stamped on its
// face. Modeled after the Scratch-original currency indicator: rim, face,
// wrapper trapezoid with paper folds, frosting dome, and a small bite of
// negative space between wrapper and frosting to read clearly at small sizes.
export class CoinIcon extends Phaser.GameObjects.Container {
  constructor(scene: Phaser.Scene, x: number, y: number, opts: CoinIconOptions = {}) {
    super(scene, x, y);
    scene.add.existing(this);

    const size = opts.size ?? 48;
    const r = size / 2;
    const g = scene.add.graphics();
    this.add(g);

    // Outer rim — dark gold
    g.fillStyle(0xa6520a, 1);
    g.fillCircle(0, 0, r);
    // Inner face — lighter orange, leaves a ~10% rim showing
    g.fillStyle(COLORS.accentOrange, 1);
    g.fillCircle(0, 0, r * 0.86);
    // Subtle highlight arc (top-left) gives the disc a coin-like sheen
    g.fillStyle(0xffba5e, 0.55);
    g.beginPath();
    g.arc(-r * 0.10, -r * 0.20, r * 0.55, Math.PI * 1.15, Math.PI * 1.85, false);
    g.fillPath();

    // Cupcake silhouette — single dark ink so it reads as a stamp.
    const ink = COLORS.headerPurpleDeep;
    g.fillStyle(ink, 1);

    // Wrapper: trapezoid with subtle taper, slightly off-center bottom.
    const wTop = r * 0.84;
    const wBot = r * 0.62;
    const wrapperTopY = r * 0.05;
    const wrapperBotY = r * 0.62;
    g.beginPath();
    g.moveTo(-wTop / 2, wrapperTopY);
    g.lineTo( wTop / 2, wrapperTopY);
    g.lineTo( wBot / 2, wrapperBotY);
    g.lineTo(-wBot / 2, wrapperBotY);
    g.closePath();
    g.fillPath();

    // Paper-fold lines on the wrapper (negative-space orange showing through).
    g.lineStyle(r * 0.07, COLORS.accentOrange, 1);
    for (const offset of [-0.20, 0.20]) {
      g.beginPath();
      g.moveTo(wTop * offset, wrapperTopY + r * 0.03);
      g.lineTo(wBot * offset, wrapperBotY - r * 0.02);
      g.strokePath();
    }
    g.lineStyle(0, 0, 0);

    // Tiny gap between wrapper and frosting helps the silhouette read.
    g.fillStyle(COLORS.accentOrange, 1);
    g.fillRect(-wTop / 2 - 1, wrapperTopY - r * 0.04, wTop + 2, r * 0.04);

    // Frosting dome — three swirly lobes on top of wrapper.
    g.fillStyle(ink, 1);
    const fY = wrapperTopY - r * 0.10;
    g.fillCircle(-r * 0.30, fY,  r * 0.22);
    g.fillCircle( r * 0.30, fY,  r * 0.22);
    g.fillCircle( 0,         fY - r * 0.10, r * 0.26);
  }
}
